import React, { createContext, useContext, useState, ReactNode } from 'react';
import withModal from '../utils/hoc/withModal';
import withSlideUpModal from '../utils/hoc/withSlideUpModalComponent';

type ConfirmationParams = {
    title?: string;
    text?: string | React.ReactNode;
    confirmButtonText?: string;
    cancelButtonText?: string;
    icon?: 'info' | 'warning' | 'error' | 'success' | 'question';
};
 
type ConfirmationResult = {
    isConfirmed?: boolean,
    cancelled?: boolean
}

type ConfirmationContextType = {
    fire: (params: ConfirmationParams) => Promise<ConfirmationResult>;
};

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

const ConfirmationBox: React.FC<ConfirmationParams & { cancel: () => void, confirm: () => void }> = ({ title, text, confirmButtonText, cancelButtonText, icon, cancel, confirm }) => {
    return (
        <React.Fragment>
            <div className="menu-title">
                <i className="fa fa-question-circle scale-box float-start me-3 ms-3 fa-3x mt-1 color-blue-dark"></i>
                <p className="color-highlight">We need to know,</p>
                <h1 className="font-20">Are you Sure?</h1>
                {/* <span onClick={cancel} className="close-menu cursor-pointer"><i className="fa color-danger fa-times-circle"></i></span> */}
            </div>
            <div className="content mt-0 pb-3">
                <p>{text}</p>
                <div className="row mb-4">
                    <div className="col-6">
                        <span
                            className="btn close-menu btn-full btn-m bg-red-dark font-600 rounded-s cursor-pointer"
                            onClick={cancel}
                        >
                            {cancelButtonText || 'Cancel'}
                        </span>
                    </div>
                    <div className="col-6">
                        <span
                            className="btn close-menu btn-full btn-m bg-green-dark font-600 rounded-s cursor-pointer"
                            onClick={confirm}
                        >
                            {confirmButtonText || 'Confirm'}
                        </span>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

const ConfirmationBoxModal = withSlideUpModal(ConfirmationBox)

export const useConfirmation = (): ConfirmationContextType => {
    const context = useContext(ConfirmationContext);
    if (!context) {
        throw new Error('useConfirmation must be used within a ConfirmationProvider');
    }
    return context;
};

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [params, setParams] = useState<ConfirmationParams | null>(null);
    const [resolvePromise, setResolvePromise] = useState<((result: ConfirmationResult) => void) | null>(null);

    const fire = (options: ConfirmationParams): Promise<ConfirmationResult> => {
        setParams(options);
        setIsVisible(true);
        return new Promise((resolve) => {
            setResolvePromise(() => resolve);
        });
    };

    const confirm = () => {
        setIsVisible(false);
        if (resolvePromise) resolvePromise({isConfirmed: true});
        clearState();
    };

    const cancel = () => {
        setIsVisible(false);
        if (resolvePromise) resolvePromise({isConfirmed: false, cancelled: true});
        clearState();
    };

    const clearState = () => {
        setParams(null);
        setResolvePromise(null);
    };

    return (
        <ConfirmationContext.Provider value={{ fire }}>
            {children}
            {isVisible && params && (
                <ConfirmationBoxModal
                    isActive={isVisible}
                    {...params}
                    cancel={cancel}
                    confirm={confirm}
                    onClose={cancel}
                />
            )}
        </ConfirmationContext.Provider>
    );
};